import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import EsgChangeLeadModal from './EsgChangeLeadModal';

const PAGE_SIZE = 20;
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name', label: 'Name A–Z' },
];

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// Infinite-scroll list of every created ESG squad, newest first (same order
// as the cohort list). Filters/search/sort are server-side; a sentinel row
// at the bottom loads the next page when it scrolls into view.
export default function EsgSquadsList({ onError, onSuccess, assignedOnly = false }) {
  const [levels, setLevels] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [levelId, setLevelId] = useState('');
  const [sort, setSort] = useState('newest');

  const [squads, setSquads] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [leadSquad, setLeadSquad] = useState(null);

  const pageRef = useRef(1);
  const requestId = useRef(0);
  const sentinelRef = useRef(null);

  useEffect(() => {
    mentorApi.getEsgLevels().then(setLevels).catch(() => {});
  }, []);

  const fetchPage = useCallback(async (page, reset) => {
    const id = ++requestId.current;
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const { items, meta } = await mentorApi.getEsgSquads({ levelId, search, sort, page, perPage: PAGE_SIZE });
      if (id !== requestId.current) return; // a newer filter change superseded this response
      pageRef.current = page;
      setSquads((prev) => (reset ? items : [...prev, ...items]));
      setTotal(meta.total);
      setHasMore(meta.has_more);
    } catch (err) {
      if (id === requestId.current) {
        setHasMore(false);
        onError?.("Couldn't load squads — try again.");
      }
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [levelId, search, sort, onError]);

  // Any filter/search/sort change restarts from page 1.
  useEffect(() => {
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading || loadingMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) fetchPage(pageRef.current + 1, false);
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, fetchPage, squads.length]);

  // Typing only edits the box; the query runs when Search is clicked/Enter is
  // pressed, so we don't hit the server on every keystroke.
  const runSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const hasFilters = Boolean(search || searchInput || levelId || sort !== 'newest');
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setLevelId('');
    setSort('newest');
  };

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row">
        <div>
          <h1 className="dashboard-title">ESG Squads</h1>
          <p className="dashboard-subtitle">
            {assignedOnly ? 'The ESG Specialist squads assigned to you' : 'Every ESG Specialist squad created so far'}{!loading && ` — ${total} found`}.
          </p>
        </div>
      </div>

      <form onSubmit={runSearch} className="esg-squads-list__form-1">
        <input
          type="search"
          className="form-control-input esg-squads-list__input-2"
          placeholder="Search squad, lead, SPOC, auditor or member name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" className="esg-btn esg-btn-secondary esg-squads-list__button-3">Search</button>
        <select className="form-control-select esg-squads-list__select-4" value={levelId} onChange={(e) => setLevelId(e.target.value)}>
          <option value="">All levels</option>
          {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select className="form-control-select esg-squads-list__select-4" value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {hasFilters && (
          <button type="button" className="esg-btn esg-btn-secondary esg-squads-list__button-3" onClick={clearFilters}>Clear</button>
        )}
      </form>

      <div className="students-table-card">
        <table className="students-table">
          <thead>
            <tr>
              <th>Squad Name</th>
              <th>Level</th>
              <th>Admin Lead</th>
              <th>SPOC</th>
              <th>Members</th>
              <th>Created</th>
              <th className="esg-squads-list__th-5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="esg-squads-list__td-6">Loading squads…</td></tr>
            )}
            {!loading && squads.length === 0 && (
              <tr><td colSpan={7} className="esg-squads-list__td-7">
                {hasFilters ? 'No squads match these filters.' : assignedOnly ? 'No squads are assigned to you yet.' : 'No squads yet. Use New Creation to form one.'}
              </td></tr>
            )}
            {!loading && squads.map((s) => (
              <tr
                key={s.id}
                tabIndex={0}
                className="esg-squads-list__tr-8"
                onClick={() => { window.location.hash = `#/esg/squad/${s.id}`; }}
                onKeyDown={(e) => { if (e.key === 'Enter') window.location.hash = `#/esg/squad/${s.id}`; }}
              >
                <td><strong className="esg-squads-list__strong-9">{s.name}</strong></td>
                <td>{s.level?.name || '—'}</td>
                <td>{s.admin_lead?.name || '—'}</td>
                <td>{s.spoc?.name || '—'}</td>
                <td>
                  <span className="checklist-row-badge">{(s.members || []).filter((m) => m.status !== 'removed').length} Students</span>
                  {s.paused_at && <span className="checklist-row-badge esg-badge-paused">Paused</span>}
                </td>
                <td>{formatDate(s.created_at)}</td>
                <td className="esg-squads-list__td-10">
                  <a
                    href={`#/esg/squad/${s.id}`}
                    className="esg-btn esg-btn-secondary esg-squads-list__a-11"
                    aria-label={`View ${s.name}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    View
                  </a>
                  <span
                    className={s.admin_lead_lock?.locked ? 'esg-tip esg-tip--end' : undefined}
                    data-tip={s.admin_lead_lock?.locked ? s.admin_lead_lock.reason : undefined}
                    style={{ marginLeft: '8px' }}
                  >
                    <button
                      type="button"
                      className="esg-btn esg-btn-secondary"
                      style={{ padding: '4px 12px', fontSize: '12px', ...(s.admin_lead_lock?.locked ? { pointerEvents: 'none' } : {}) }}
                      aria-label={`Change lead for ${s.name}`}
                      disabled={Boolean(s.admin_lead_lock?.locked)}
                      onClick={(e) => { e.stopPropagation(); setLeadSquad(s); }}
                    >
                      Change lead
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div ref={sentinelRef} className="esg-squads-list__div-12" />
        {loadingMore && (
          <div className="esg-squads-list__div-13">Loading more…</div>
        )}
        {leadSquad && (
          <EsgChangeLeadModal
            squad={leadSquad}
            onClose={() => setLeadSquad(null)}
            onSuccess={onSuccess}
            onChanged={(result) => setSquads((prev) => prev.map((x) => (
              x.id === leadSquad.id ? { ...x, admin_lead: result.admin_lead, admin_lead_student_id: result.admin_lead_student_id } : x
            )))}
          />
        )}
        {!loading && !hasMore && squads.length > 0 && (
          <div className="esg-squads-list__div-14">End of list</div>
        )}
      </div>
    </div>
  );
}
